import axios from 'axios';
import { Subject } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { Gizmo } from '../../gizmos';
import {
  IError,
  IGizmo,
  IGizmoData,
  IGizmoEvent,
  IUserV2,
  ICodeScanner,
  IBinderSetDataParams,
  TGetInfoCB,
} from '../../types';
import { downloadFileS3 } from './utils';
import { toCamelCase } from '../../utils';
import CommonBinder from '../common-binder';

interface IMultimediaFieldBinder {
  init(): Promise<void>;
}

class MultimediaFieldBinder extends CommonBinder
  implements IMultimediaFieldBinder {
  private _files: File[];
  public files: Subject<File[]>;
  /**
   * Variables to control de code scanner in the UI
   */
  private _codeScanner: ICodeScanner;
  public codeScanner: Subject<ICodeScanner>;
  /**
   * Message to be displayed in the UI layer
   */
  public message: Subject<string>;

  constructor(gizmo: Gizmo) {
    super(gizmo);

    this._files = [];
    this.files = new Subject<File[]>();
    this.codeScanner = new Subject<ICodeScanner>();
    this.message = new Subject<string>();

    this._codeScanner = {
      show: false,
      label: 'Leer QR',
    };
  }

  public init = (): Promise<void> => {
    return new Promise<void>(async resolve => {
      const apiUrl: string | undefined = this._gizmo.getDao()?.getApiUrl();
      const user: IUserV2 | undefined = this._gizmo.getDao()?.getUser();
      const data = this._gizmo.getData();

      if (data && !Array.isArray(data) && apiUrl && user) {
        const platformInterface = this._gizmo
          .getFoundation()
          ?.getPlatformInterface();

        let file: File | undefined;
        if (
          platformInterface &&
          data.fd?.attachmentId &&
          data.fd.fileExtension
        ) {
          const result = await platformInterface.fileGet({
            path: '',
            fileFid: data.fd?.attachmentId,
            fileExtension: data.fd?.fileExtension,
          });

          file = result?.file;
        }

        // await this._downloadFileV2(data);
        // this._downloadFileV2(data).then(() => {});
        if (file) {
          this._files = [file];
          this.files.next(this._files);
        } else {
          this._downloadFileS3(data).then(file => {
            if (file) {
              this._files = [file];
              this.files.next(this._files);
            }
          });
        }
      }

      resolve();
    });
  };

  /**
   * Get code scanner current settings
   * @returns
   */
  public getCodeScanner = (): ICodeScanner => {
    return this._codeScanner;
  };

  /**
   * Set code scanner reading results
   */
  public setCodeScannerResult = (result: string): Promise<void> => {
    return new Promise<void>(async resolve => {
      const config = this._gizmo.getConfig();

      if (config.ops?.fileField?.serverProcessing?.type === 'curp' && result) {
        if (result.includes('|')) {
          try {
            const curpParts = result.split('|');

            if (curpParts.length >= 10) {
              const curpResult = {
                curp: curpParts[0],
                name: curpParts[4],
                last_name: curpParts[2],
                second_last_name: curpParts[3],
                sex: curpParts[5],
                birth_date: curpParts[6],
                entity: curpParts[7],
                entity_code: curpParts[8],
              };

              await this._processCurpResponse({ curp: curpResult });

              this._codeScanner = {
                ...this._codeScanner,
                show: false,
              };
              this.codeScanner.next(this._codeScanner);
              this.message.next('');
              this._gizmo.getErrors().next([]);
            }
          } catch (e) {
            this._codeScanner = {
              ...this._codeScanner,
              show: true,
            };
            this._gizmo.getErrors().next([
              {
                fid: uuid(),
                code: 'file_field.server-processing',
                message: 'El código QR no es válido',
              },
            ]);
          }
        } else {
          this._codeScanner = {
            ...this._codeScanner,
            show: true,
          };
          this._gizmo.getErrors().next([
            {
              fid: uuid(),
              code: 'file_field.server-processing',
              message: 'El código QR no es válido',
            },
          ]);
        }
      }

      resolve();
    });
  };

  /**
   * Used to set a file on the gizmo, right now it's used in the handleGizmoEvents function when
   * handling an event of type 'gizmo.set-data'
   *
   * @param {File} value file that's going to be set on the gizmo
   * @returns
   */
  public setValue = (value?: File): Promise<void> => {
    return new Promise<void>(async resolve => {
      const v: any = value;

      if (value !== undefined) {
        // When the file is coming from the mobile layer, it will have the attachmentId field
        // and we have to copy the file through the platform interface
        if (v.attachmentId) {
          const fileExtension = v.name.split('.').pop();
          const newFileFid = uuid();
          this._gizmo
            .getFoundation()
            ?.getPlatformInterface()
            ?.fileCopy({
              path: '',
              originalFileFid: v.attachmentId,
              originalFileExtension: fileExtension,
              newFileFid,
              newFileExtension: fileExtension,
            });
        }

        await this.setFiles([value]);
      } else {
        await this.setFiles([]);
      }
      resolve();
    });
  };

  /**
   * Set data to the gizmo
   * @param data gizmo data to be set
   */
  public setData = async ({ data, event }: IBinderSetDataParams) => {
    const rootGizmoConfig = event?.data?.gizmoSetData?.rootGizmoConfig;
    const rootGizmoData = event?.data?.gizmoSetData?.rootGizmoData;
    const destiny = event?.data?.gizmoSetData?.destiny;

    let gizmoConfig: IGizmo | undefined;
    let originFid = destiny.originFid;
    if (
      !originFid &&
      destiny.originUid &&
      rootGizmoConfig?.gizmos &&
      rootGizmoConfig.gizmos.length > 0
    ) {
      gizmoConfig = rootGizmoConfig.gizmos.find(
        (gizmo: any) => gizmo.uid === destiny.originUid
      );
    } else if (originFid && rootGizmoConfig?.gizmos) {
      gizmoConfig = rootGizmoConfig.gizmos.find(
        (gizmo: any) => gizmo.fid === originFid
      );
    }

    const getInfoCB: TGetInfoCB = ({ type }) => {
      if (type === 'rootGizmoFid' && rootGizmoConfig) {
        return rootGizmoConfig.fid;
      } else if (type === 'rootGizmoDataFid' && rootGizmoData) {
        return rootGizmoData.fid;
      }

      return undefined;
    };

    // We're creating a Gizmo shell in order for the download file function to use it
    const gizmo: any = {
      getDao: this._gizmo.getDao,
      getCallbacks: () => ({
        getInfoCB,
      }),
      getConfig: () => gizmoConfig,
    };

    const file = await downloadFileS3({
      data,
      context: {
        gizmo,
      },
    });

    if (file) {
      await this.setFiles([file]);
    }
  };

  /**
   * Function used to set the files from the UI layer
   *
   * @param {File[]} files files being set by the UI layer
   * @returns
   */
  public setFiles = (
    files: File[],
    source?: 'local_file' | 'camera'
  ): Promise<void> => {
    return new Promise<void>(async resolve => {
      const platformInterface = this._gizmo
        .getFoundation()
        ?.getPlatformInterface();

      // Right now we're going to deal with only one file
      if (files.length === 1) {
        this._files = files;
        this.files.next(this._files);
        const file = files[0];
        const fileName = file.name;
        const fileExtension = fileName.split('.').pop();

        let attachmentId = uuid();
        const f: any = file;
        // This field will happen when the platform layer is mobile. This happens because of the
        // file library, we have to store the file before sending it to the core
        if (f.attachmentId) {
          attachmentId = f.attachmentId;
        }

        if (platformInterface && fileExtension) {
          platformInterface.fileSet({
            path: '',
            fileFid: attachmentId,
            fileName,
            fileExtension,
            file,
          });
        }

        await this._gizmo.setData({
          v: attachmentId,
          fd: {
            attachmentId,
            fileName,
            fileExtension,
            fileSize: file.size,
            fileType: file.type,
          },
        });

        const config = this._gizmo.getConfig();
        if (config.ops?.fileField?.serverProcessing) {
          const apiUrl = this._gizmo.getDao()?.getApiUrl();
          const userCredentials: any =
            this._gizmo.getDao()?.getUser()?.credentials || {};

          if (apiUrl && userCredentials) {
            try {
              const url = `${apiUrl}/main_api/v1/files/root_gizmo_data/process`;
              const formData = new FormData();
              formData.append('gizmo', JSON.stringify(this._gizmo.getConfig()));
              formData.append('file', file);

              const response = await axios.post(url, formData, {
                headers: {
                  ...userCredentials,
                  'Content-Type': 'multipart/form-data',
                },
              });

              if (response.status === 200) {
                const { data } = response;

                if (config.ops.fileField.serverProcessing.type === 'curp') {
                  this._processCurpResponse(data);
                  this._gizmo.getErrors().next([]);
                }
              }
            } catch (e) {
              if (e.response.status === 400) {
                const { data } = e.response;

                if (data.detail) {
                  const error: IError = {
                    fid: uuid(),
                    code: 'file_field.server-processing',
                    message: data.detail,
                  };

                  this._gizmo.getErrors().next([error]);

                  if (source === 'camera') {
                    this._codeScanner = {
                      label: 'Leer QR',
                      show: true,
                    };
                    this.codeScanner.next(this._codeScanner);
                    this.message.next(
                      'La fotografía no es tan clara. Por favor escanea el QR ubicado arriba a la derecha.'
                    );
                  }
                }
              }
            }
          }
        }
      } else if (files.length === 0) {
        const config = this._gizmo.getConfig();
        const data = this._gizmo.getData();

        if (
          data &&
          !Array.isArray(data) &&
          platformInterface &&
          data.fd?.attachmentId
        ) {
          const { attachmentId, fileExtension } = data.fd;

          if (config.ops?.fileField?.serverProcessing?.curp) {
            // Send event to all the gizmos that are configured to receive the curp data
            // so they will clear it
            const curpConfig: any = config.ops.fileField.serverProcessing.curp;

            for (const key in curpConfig) {
              const event: IGizmoEvent = {
                type: 'gizmo.clear-data',
                src: {
                  type: 'gizmo',
                  gizmoFid: config.fid,
                  gizmoUid: config.uid,
                },
                dest: {
                  type: 'gizmo',
                  gizmoUid: curpConfig[key],
                },
              };

              this._gizmo.getEvents().next(event);
            }
          }

          await platformInterface.fileRemove({
            path: '',
            fileFid: attachmentId,
            fileExtension: fileExtension || 'jpg',
          });
        }
        await this._gizmo.setData(undefined);
      }

      resolve();
    });
  };

  /**
   * Get files stored in memory
   */
  public getFiles = (): File[] => {
    return this._files;
  };

  public _downloadFileS3 = async (
    data: IGizmoData
  ): Promise<File | undefined> => {
    return await downloadFileS3({
      data,
      context: {
        gizmo: this._gizmo,
      },
    });
  };

  /**
   * Downloads file from the server from the v2 api
   *
   * @param {IGizmoData} data gizmo data
   * @returns
   */
  public _downloadFileV2 = async (data: IGizmoData): Promise<void> => {
    return new Promise<void>(async resolve => {
      const user: IUserV2 | undefined = this._gizmo.getDao()?.getUser();
      const apiUrl: string | undefined = this._gizmo.getDao()?.getApiUrl();
      const callbacks = this._gizmo.getCallbacks();
      const config: IGizmo = this._gizmo.getConfig();

      const formId = callbacks.getInfoCB({ type: 'rootGizmoFid' });
      const filledFormId = callbacks.getInfoCB({ type: 'rootGizmoDataFid' });
      const fId = config.fid;
      const answerIndex = '0';
      const attachmentId = data.fd?.attachmentId;
      const extension = data.fd?.fileExtension || 'jpg';

      if (apiUrl && user) {
        const credentials: any = user.credentials;
        let url = `${apiUrl}/v3/forms/${formId}/filled_forms_attachment?`;
        url += `attachmentId=${attachmentId}&formId=${formId}&filledFormId=${filledFormId}&`;
        url += `fId=${fId}&answerIndex=${answerIndex}&extension=${extension}`;

        try {
          const response = await axios({
            method: 'GET',
            url,
            responseType: 'blob',
            headers: {
              ...credentials,
            },
          });

          if (response.status === 200 || response.status === 201) {
            const file = new File(
              [response.data],
              `${attachmentId}.${extension}`,
              {
                type: 'image/jpeg',
              }
            );

            this._files = [file];
            this.files.next(this._files);
          }
        } catch (e) {}
      }

      resolve();
    });
  };

  /**
   * Process the response from the server when the file is processed. We're going to generate
   * the events to set the data in the gizmos that are configured.
   *
   * @param {any} data data arriving from the server
   * @returns
   */
  private _processCurpResponse = async (data: any): Promise<void> => {
    return new Promise<void>(async resolve => {
      const gizmoConfig = this._gizmo.getConfig();

      if (gizmoConfig.ops?.fileField?.serverProcessing?.curp && data.curp) {
        const curpCamelCase = toCamelCase(data.curp);
        const curpConfig: any = gizmoConfig.ops.fileField.serverProcessing.curp;

        for (const key in curpConfig) {
          const gizmoUid = curpConfig[key];
          const event: IGizmoEvent = {
            type: 'gizmo.set-data',
            src: {
              type: 'gizmo',
              gizmoUid: gizmoConfig.uid,
            },
            dest: {
              type: 'gizmo',
              gizmoUid,
            },
            data: {
              gizmoSetData: {
                value: curpCamelCase[key.replace('GizmoUid', '')],
              },
            },
          };

          this._gizmo.getEvents().next(event);
        }
      }

      resolve();
    });
  };
}

export default MultimediaFieldBinder;
